import { Component, computed, inject, input, model } from "@angular/core";
import { FilterPanelComponent } from "../../../../../../shared/components/filter-panel/filter-panel.component";
import { FilterSelection } from "../../../../../../shared/components/filter-panel/filter-panel.model";
import { LotPrimaryIdentifier } from "../../../../../../core/feature-flags/models/AdminConfigDto";
import { ScheduleLotCard } from "../../models/ScheduleLotCard";
import {
  ScheduleFilterDefinition,
  ScheduleFilterService,
  ScheduleSortDirection,
  SORT_SECTION_ID
} from "../../services/schedule-filter.service";

/**
 * Adapter around the shared filter sheet: adds the sort order as a section alongside BQ Step and
 * Financial Community, and splits it back out of the applied selection.
 */
@Component({
  selector: "app-schedule-filter-panel",
  standalone: true,
  imports: [FilterPanelComponent],
  templateUrl: "./schedule-filter-panel.component.html"
})
export class ScheduleFilterPanelComponent {
  isOpen = model<boolean>(false);
  /** Every card the two tabs can render; the sections' options come from these. */
  cards = input<ScheduleLotCard[]>([]);
  lotPrimaryIdentifier = input<LotPrimaryIdentifier>(LotPrimaryIdentifier.LotBlock);

  protected readonly filterService = inject(ScheduleFilterService);

  readonly sortDefinition = computed<ScheduleFilterDefinition>(() => {
    const identifier = this.lotPrimaryIdentifier();

    return {
      id: SORT_SECTION_ID,
      label: "Sort Order",
      isChoice: true,
      getValue: () => null,
      availableValues: this.filterService.getSortOptions(identifier).map(option => option.direction),
      getLabel: value => this.filterService.getSortOptionLabel(identifier, value),
      getChoiceLabel: value => this.filterService.getSortOptionShortLabel(identifier, value),
      // Already ordered ascending then descending; the default label sort would shuffle them.
      compareOptions: () => 0
    };
  });

  readonly panelDefinitions = computed<ScheduleFilterDefinition[]>(() =>
    [this.sortDefinition(), ...this.filterService.definitions]);

  readonly panelSelected = computed<FilterSelection>(() => ({
    ...this.filterService.selected(),
    [SORT_SECTION_ID]: [this.filterService.sortDirection()]
  }));

  onApplied(selected: FilterSelection): void {
    const { [SORT_SECTION_ID]: sort, ...filters } = selected;

    this.filterService.applyDraft(
      filters,
      (sort?.[0] as ScheduleSortDirection) ?? ScheduleSortDirection.Ascending
    );
  }
}
